import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../../components/templates/DashboardLayout.js';
import { Typography } from '../../components/atoms/Typography.js';
import { Badge } from '../../components/atoms/Badge.js';
import { Button } from '../../components/atoms/Button.js';
import { Spinner } from '../../components/atoms/Spinner.js';
import { ErrorBanner } from '../../components/molecules/ErrorBanner.js';
import { ReportViewer } from '../../components/organisms/ReportViewer.js';
import { DiscrepancyList } from '../../components/organisms/DiscrepancyList.js';
import { QuestionList } from '../../components/organisms/QuestionList.js';
import { useDashboardStore } from '../../stores/dashboard-store.js';
import { get, post } from '../../lib/api-client.js';
import type { ApiResponse } from '../../types/api.js';
import type { Survey, Discrepancy } from '../../types/survey.js';

interface ReportData {
  id: string;
  surveyId: string;
  summary: string;
  discrepancies: Discrepancy[];
  generatedAt: string;
}

const statusVariantMap: Record<string, 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
  draft: 'neutral',
  active: 'info',
  paused: 'warning',
  completed: 'success',
  archived: 'neutral',
};

export function ReportPage() {
  const { t, i18n } = useTranslation('dashboard');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setSelectedSurvey } = useDashboardStore();

  const [survey, setSurvey] = useState<Survey | null>(null);
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSurvey = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await get<ApiResponse<Survey>>(`/api/v1/surveys/${id}`);
      setSurvey(res.data);
      setSelectedSurvey(res.data as unknown as Record<string, unknown>);

      try {
        const reportRes = await get<ApiResponse<ReportData>>(`/api/v1/surveys/${id}/report`);
        // Only set report if it contains actual content
        if (reportRes.data?.summary) {
          setReport(reportRes.data);
        }
      } catch {
        // Report not yet generated - this is fine
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('report.fetch_error'));
    } finally {
      setLoading(false);
    }
  }, [id, setSelectedSurvey, t]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const handleGenerateReport = async () => {
    if (!id) return;
    setReportLoading(true);
    try {
      await post(`/api/v1/surveys/${id}/regenerate-report`);
      // Refresh survey data to get updated status
      await fetchSurvey();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('report.generate_error'));
    } finally {
      setReportLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            {t('report.back')}
          </Button>
          <ErrorBanner message={error} onRetry={fetchSurvey} />
        </div>
      </DashboardLayout>
    );
  }

  if (!survey) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            {t('report.back')}
          </Button>
          <Typography variant="body">{t('report.not_found')}</Typography>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard')}>
          {t('report.back')}
        </Button>

        {/* Survey Overview */}
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Typography variant="h2">{survey.title}</Typography>
              {survey.description && (
                <Typography variant="body-sm" className="mt-1">
                  {survey.description}
                </Typography>
              )}
            </div>
            <Badge variant={statusVariantMap[survey.status] ?? 'neutral'}>
              {t(`survey_status.${survey.status}`)}
            </Badge>
          </div>

          <div className="mt-4 flex flex-wrap gap-6 text-sm text-neutral-500">
            <div>
              <span className="font-medium text-neutral-700">{t('report.created_at')}</span>
              {new Date(survey.createdAt).toLocaleDateString(
                i18n.language === 'ja' ? 'ja-JP' : 'en-US',
              )}
            </div>
            <div>
              <span className="font-medium text-neutral-700">{t('report.question_count')}</span>
              {survey.questions?.length ?? 0}
            </div>
          </div>
        </div>

        {/* Report Section */}
        {report ? (
          <>
            <ReportViewer content={report.summary} generatedAt={report.generatedAt} />

            {report.discrepancies && report.discrepancies.length > 0 && (
              <DiscrepancyList discrepancies={report.discrepancies} />
            )}
          </>
        ) : (
          <div className="rounded-xl border border-neutral-200 bg-white p-6 text-center">
            <Typography variant="body-sm" className="mb-4">
              {t('report.not_generated')}
            </Typography>
            <Button onClick={handleGenerateReport} loading={reportLoading}>
              {t('report.generate')}
            </Button>
          </div>
        )}

        {/* Questions */}
        {survey.questions && survey.questions.length > 0 && (
          <QuestionList questions={survey.questions} />
        )}
      </div>
    </DashboardLayout>
  );
}
